import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, X, WarningCircle } from '@phosphor-icons/react';
import { api, type Session } from './api';
import i18n from './i18n';
import { desktopBridge, isDesktop, platformState, subscribePlatform } from './lib/client-platform';
import { siteFavicons } from './lib/site-favicons';
import { clearDesktopSearchStorage } from './lib/desktop-search';
import { clearCollectionFilterPreferences } from './lib/collection-filter-preferences';

export const sessionKey = ['session'] as const;
export function useSession() {
  return useQuery({
    queryKey: sessionKey,
    queryFn: api.session,
    staleTime: 60_000,
    retry: 1,
  });
}
type ToastOptions = {
  duration?: number;
  action?: { label: string; run: () => Promise<unknown> | void };
};
type Toast = ToastOptions & { id: number; text: string; error?: boolean };
const ToastContext = createContext<(text: string, error?: boolean, options?: ToastOptions) => void>(
  () => {},
);
export function useToast() {
  return useContext(ToastContext);
}
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const client = useQueryClient();
  useEffect(() => {
    let expired = false;
    const onExpired = () => {
      if (expired) return;
      expired = true;
      clearDesktopSearchStorage();
      clearCollectionFilterPreferences();
      siteFavicons.identify(location.origin, '');
      client.clear();
      window.location.assign('/login?session=expired');
    };
    window.addEventListener('kiipu:session-expired', onExpired);
    return () => window.removeEventListener('kiipu:session-expired', onExpired);
  }, [client]);
  function add(text: string, error = false, options: ToastOptions = {}) {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev.slice(-2), { id, text, error, ...options }]);
  }
  return (
    <ToastContext.Provider value={add}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map((toast) => (
          <ToastMessage
            key={toast.id}
            toast={toast}
            onDismiss={() => setToasts((prev) => prev.filter((entry) => entry.id !== toast.id))}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
function ToastMessage({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const remaining = useRef(toast.duration ?? 6000);
  const paused = hovered || focused || busy;
  useEffect(() => {
    if (paused) return;
    const start = Date.now();
    const timer = window.setTimeout(onDismiss, remaining.current);
    return () => {
      clearTimeout(timer);
      remaining.current = Math.max(0, remaining.current - (Date.now() - start));
    };
  }, [paused, onDismiss]);
  return (
    <div
      className={`toast${toast.error || error ? ' toast-error' : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
    >
      {toast.error || error ? <WarningCircle size={18} /> : <CheckCircle size={18} />}
      <span>{error || toast.text}</span>
      {toast.action ? (
        <button
          className="toast-action"
          disabled={busy}
          aria-busy={busy || undefined}
          onClick={async () => {
            setBusy(true);
            setError('');
            try {
              await toast.action!.run();
              onDismiss();
            } catch (problem) {
              setError(problem instanceof Error ? problem.message : '操作未完成，请重试');
              remaining.current = toast.duration ?? 8000;
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? '正在恢复…' : toast.action.label}
        </button>
      ) : null}
      <button aria-label="关闭通知" disabled={busy} onClick={onDismiss}>
        <X size={14} />
      </button>
    </div>
  );
}
export function useAppearance(enabled: boolean) {
  const query = useQuery({
    queryKey: ['settings'],
    queryFn: api.settings,
    enabled,
    staleTime: 60_000,
  });
  useEffect(() => {
    const theme = query.data?.theme;
    if (!theme) return;
    const media = matchMedia('(prefers-color-scheme: dark)');
    const apply = () => {
      document.documentElement.dataset.theme =
        theme === 'system' ? (media.matches ? 'dark' : 'light') : theme;
    };
    try {
      localStorage.setItem('kiipu.appearance.v1', theme);
    } catch {}
    apply();
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, [query.data?.theme]);
  useEffect(() => {
    if (query.data?.locale) {
      void i18n.changeLanguage(query.data.locale);
      document.documentElement.lang = query.data.locale;
    }
  }, [query.data?.locale]);
  return query;
}
export function useLogout() {
  const client = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: api.logout,
    onSuccess: () => {
      clearDesktopSearchStorage();
      clearCollectionFilterPreferences();
      window.dispatchEvent(new Event('kiipu:clear-search'));
      client.clear();
      window.location.assign('/login');
    },
    onError: (error) => toast(error.message, true),
  });
}
export function useLibraryEvents(userId?: string) {
  const client = useQueryClient();
  useEffect(() => {
    const synchronize = () => {
      const native = platformState();
      siteFavicons.identify(
        native?.apiOrigin ?? location.origin,
        isDesktop() ? (native?.user?.id ?? '') : (userId ?? ''),
      );
      siteFavicons.setAvailability(
        native?.online ?? navigator.onLine,
        document.visibilityState !== 'hidden' && (native?.active ?? true),
      );
    };
    synchronize();
    const focus = () => {
      synchronize();
      siteFavicons.refresh(true);
    };
    const stop = subscribePlatform(synchronize);
    window.addEventListener('online', synchronize);
    window.addEventListener('offline', synchronize);
    window.addEventListener('focus', focus);
    document.addEventListener('visibilitychange', synchronize);
    return () => {
      stop();
      window.removeEventListener('online', synchronize);
      window.removeEventListener('offline', synchronize);
      window.removeEventListener('focus', focus);
      document.removeEventListener('visibilitychange', synchronize);
    };
  }, [userId]);
  useEffect(() => {
    if (!userId) return;
    const target = new EventTarget();
    const bridge = desktopBridge();
    const stop = bridge?.onEvent((event) => {
      if (event.type === 'stream')
        target.dispatchEvent(new MessageEvent(event.event, { data: event.data }));
    });
    const source = bridge ? target : new EventSource('/api/v1/events', { withCredentials: true });
    const refreshPreferences = () => {
      window.dispatchEvent(new Event('kiipu:collection-filters-changed'));
    };
    source.addEventListener('collection-filters.changed', refreshPreferences);
    let timer: ReturnType<typeof setTimeout> | undefined;
    let baseline: number | undefined;
    const refresh = (event: MessageEvent) => {
      siteFavicons.refresh();
      try {
        const value = JSON.parse(event.data) as { version?: number };
        if (typeof value.version === 'number') {
          const previous = baseline;
          baseline = value.version;
          if (previous === undefined || previous === value.version) return;
        }
      } catch {
        /* Named events may have a non-version payload. */
      }
      clearTimeout(timer);
      timer = setTimeout(() => {
        const invalidate = () => {
          void client.invalidateQueries({ queryKey: ['items'] });
          void client.invalidateQueries({ queryKey: ['item'] });
          void client.invalidateQueries({ queryKey: ['usage'] });
          void client.invalidateQueries({ queryKey: ['folders'] });
          void client.invalidateQueries({ queryKey: ['tags'] });
        };
        invalidate();
      }, 250);
    };
    source.addEventListener('message', refresh as EventListener);
    for (const name of [
      'changed',
      'capture.completed',
      'capture.updated',
      'item.updated',
      'content-changed',
    ])
      source.addEventListener(name, refresh as EventListener);
    return () => {
      stop?.();
      source.removeEventListener('collection-filters.changed', refreshPreferences);
      if (!isDesktop()) (source as EventSource).close();
      clearTimeout(timer);
    };
  }, [userId, client]);
}
